import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: 'eaux',
    subCategory: '',
    format: '',
    price: '',
    oldPrice: '',
    stock: '',
    description: '',
    isPremium: false,
    isBestSeller: false,
    imageUrl: '',
    weight: ''
  });

  // Fonction de compression d'image
  const compressImage = (base64) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const maxWidth = 300;
        const maxHeight = 300;
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (maxHeight / height) * width;
          height = maxHeight;
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = base64;
    });
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Nettoyer les données avant envoi
      const cleanData = {
        name: formData.name || '',
        brand: formData.brand || null,
        category: formData.category || 'eaux',
        subCategory: formData.subCategory || null,
        format: formData.format || null,
        price: formData.price ? parseFloat(formData.price) : 0,
        oldPrice: formData.oldPrice ? parseFloat(formData.oldPrice) : null,
        stock: formData.stock ? parseInt(formData.stock) : 0,
        description: formData.description || null,
        imageUrl: formData.imageUrl || null,
        isPremium: formData.isPremium || false,
        isBestSeller: formData.isBestSeller || false,
        weight: formData.weight || null,
        isActive: true
      };
      
      console.log('📦 Données nettoyées envoyées:', cleanData);
      
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.id}`, cleanData);
      } else {
        await api.post('/admin/products', cleanData);
      }
      fetchProducts();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      fetchProducts();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setImagePreview(null);
    setFormData({
      name: '',
      brand: '',
      category: 'eaux',
      subCategory: '',
      format: '',
      price: '',
      oldPrice: '',
      stock: '',
      description: '',
      isPremium: false,
      isBestSeller: false,
      imageUrl: '',
      weight: ''
    });
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      brand: product.brand || '',
      category: product.category || 'eaux',
      subCategory: product.sub_category || '',
      format: product.format || '',
      price: product.price || '',
      oldPrice: product.old_price || '',
      stock: product.stock || '',
      description: product.description || '',
      isPremium: product.is_premium || false,
      isBestSeller: product.is_best_seller || false,
      imageUrl: product.image_url || '',
      weight: product.weight || ''
    });
    setImagePreview(product.image_url);
    setShowModal(true);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const compressed = await compressImage(reader.result);
          setImagePreview(compressed);
          setFormData({ ...formData, imageUrl: compressed });
        } catch (error) {
          console.error('Erreur compression:', error);
          setImagePreview(reader.result);
          setFormData({ ...formData, imageUrl: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const getStockStatus = (stock) => {
    if (stock === 0) return { text: 'Rupture', color: 'bg-red-100 text-red-600' };
    if (stock < 10) return { text: 'Stock faible', color: 'bg-yellow-100 text-yellow-600' };
    return { text: 'En stock', color: 'bg-green-100 text-green-600' };
  };

  const getCategoryIcon = (category) => {
    if (category === 'eaux') {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 2v2M12 4c-3.5 0-6 2.5-6 6v8h12v-8c0-3.5-2.5-6-6-6z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6M12 9v6" />
        </svg>
      );
    } else if (category === 'sodas') {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3h14v3H5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 6v10c0 2.5 2 4 5 4s5-1.5 5-4V6" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6" />
        </svg>
      );
    } else {
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 3h10l1 5H6z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 8v9c0 2 1 3 4 3s4-1 4-3V8" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 12h4" />
        </svg>
      );
    }
  };

  const filteredProducts = products.filter(product => {
    const matchSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        product.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'price-asc') return (a.price || 0) - (b.price || 0);
    if (sortBy === 'price-desc') return (b.price || 0) - (a.price || 0);
    if (sortBy === 'stock') return (a.stock || 0) - (b.stock || 0);
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-500">Chargement des produits...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Gestion des produits</h1>
          <p className="text-gray-500 mt-1">Ajoutez, modifiez ou supprimez des produits</p>
        </div>
        <button 
          onClick={() => { resetForm(); setShowModal(true); }} 
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter un produit
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button 
              onClick={() => setCategoryFilter('all')} 
              className={`px-4 py-2 rounded-lg transition ${categoryFilter === 'all' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Tous
            </button>
            <button 
              onClick={() => setCategoryFilter('eaux')} 
              className={`px-4 py-2 rounded-lg transition ${categoryFilter === 'eaux' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Eaux
            </button>
            <button 
              onClick={() => setCategoryFilter('sodas')} 
              className={`px-4 py-2 rounded-lg transition ${categoryFilter === 'sodas' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Sodas
            </button>
            <button 
              onClick={() => setCategoryFilter('laits')} 
              className={`px-4 py-2 rounded-lg transition ${categoryFilter === 'laits' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Laits
            </button>
          </div>
          <div className="flex gap-3">
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)} 
              className="px-3 py-2 border rounded-lg"
            >
              <option value="name">Trier par nom</option>
              <option value="price-asc">Prix croissant</option>
              <option value="price-desc">Prix décroissant</option>
              <option value="stock">Stock</option>
            </select>
            <div className="relative">
              <input 
                type="text" 
                placeholder="Rechercher..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="pl-10 pr-4 py-2 border rounded-lg w-64" 
              />
              <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Image</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Nom</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Catégorie</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Prix</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Stock</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedProducts.map(product => {
                const stockStatus = getStockStatus(product.stock);
                return (
                  <tr key={product.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          getCategoryIcon(product.category)
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.brand || ''}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-600`}>
                        {product.category === 'eaux' ? 'Eaux' : product.category === 'sodas' ? 'Sodas' : 'Laits'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-bold">{product.price} €</p>
                        {product.old_price && <p className="text-xs text-gray-400 line-through">{product.old_price} €</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${stockStatus.color}`}>{stockStatus.text}</span>
                      <p className="text-xs text-gray-400 mt-1">{product.stock || 0} unités</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${product.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                        {product.is_active !== false ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => editProduct(product)} className="text-blue-600 hover:text-blue-800">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button onClick={() => setShowDeleteConfirm(product.id)} className="text-red-600 hover:text-red-800">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Confirmer la suppression</h2>
            <p className="text-gray-600 mb-6">Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(showDeleteConfirm)} className="flex-1 bg-red-600 text-white py-2 rounded-lg">Supprimer</button>
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 bg-gray-200 py-2 rounded-lg">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">{editingProduct ? 'Modifier' : 'Ajouter'} un produit</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Image</label>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                        {imagePreview ? (
                          <img src={imagePreview} alt="Aperçu" className="w-full h-full object-cover" />
                        ) : (
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                      <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition">
                        Choisir une image
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Image compressée automatiquement</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Poids</label>
                    <input type="text" placeholder="7.5 kg" value={formData.weight} onChange={(e) => setFormData({...formData, weight: e.target.value})} className="w-full p-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full p-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Marque</label>
                    <input type="text" value={formData.brand} onChange={(e) => setFormData({...formData, brand: e.target.value})} className="w-full p-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Catégorie *</label>
                    <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full p-2 border rounded-lg" required>
                      <option value="eaux">Eaux</option>
                      <option value="sodas">Sodas</option>
                      <option value="laits">Laits</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Sous-catégorie</label>
                    <input type="text" placeholder="Ex: plates, gazeuses..." value={formData.subCategory} onChange={(e) => setFormData({...formData, subCategory: e.target.value})} className="w-full p-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Format</label>
                    <input type="text" placeholder="6 × 1,5 L" value={formData.format} onChange={(e) => setFormData({...formData, format: e.target.value})} className="w-full p-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Prix (€) *</label>
                    <input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full p-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Ancien prix (€)</label>
                    <input type="number" step="0.01" value={formData.oldPrice} onChange={(e) => setFormData({...formData, oldPrice: e.target.value})} className="w-full p-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Stock</label>
                    <input type="number" value={formData.stock} onChange={(e) => setFormData({...formData, stock: e.target.value})} className="w-full p-2 border rounded-lg" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea rows="3" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full p-2 border rounded-lg"></textarea>
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.isPremium} onChange={(e) => setFormData({...formData, isPremium: e.target.checked})} />
                    Produit Premium
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.isBestSeller} onChange={(e) => setFormData({...formData, isBestSeller: e.target.checked})} />
                    Best Seller
                  </label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-primary text-white py-2 rounded-lg">{editingProduct ? 'Mettre à jour' : 'Ajouter'}</button>
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-gray-200 py-2 rounded-lg">Annuler</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;